/**
 * Per-page <title>, description and canonical for the blog.
 *
 * The app shipped with none of these: every route served the one <title> baked
 * into public/index.html and no canonical at all, so all 40-odd posts looked
 * like the same document to a crawler. Moving under /blogi is the moment to fix
 * it - the canonical is what tells Google the subfolder URL is the real home of
 * a post whose subdomain URL it already knows.
 *
 * Client-side is enough here: Googlebot renders the page before indexing it, and
 * this app has no server to render tags into the HTML.
 */
import { useEffect } from "react";

const SITE_NAME = "Laatulakki";

const setMeta = (selector, create, content) => {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(el.tagName === "LINK" ? "href" : "content", content);
};

/**
 * @param {object} seo
 * @param {string} seo.title    page title, without the site-name suffix
 * @param {string} [seo.description]
 * @param {string} [seo.path]   app-absolute path, e.g. "/post/slug"; defaults to
 *                              the current location, which is already correct
 *                              for whatever base the app is mounted at.
 */
export function useSeo({ title, description, path } = {}) {
  useEffect(() => {
    if (title) document.title = `${title} | ${SITE_NAME}`;

    setMeta(
      'meta[name="description"]',
      () => Object.assign(document.createElement("meta"), { name: "description" }),
      description
    );

    // Keeps ?page=N: a paginated listing is its own page, not a duplicate of
    // the first one.
    const url =
      typeof window === "undefined"
        ? null
        : `${window.location.origin}${
            path || `${window.location.pathname}${window.location.search}`
          }`;

    setMeta(
      'link[rel="canonical"]',
      () => Object.assign(document.createElement("link"), { rel: "canonical" }),
      url
    );
    setMeta(
      'meta[property="og:url"]',
      () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:url");
        return el;
      },
      url
    );
    setMeta(
      'meta[property="og:title"]',
      () => {
        const el = document.createElement("meta");
        el.setAttribute("property", "og:title");
        return el;
      },
      title && `${title} | ${SITE_NAME}`
    );
  }, [title, description, path]);
}

export default useSeo;
